import { DOCUMENT, Injectable, effect, inject } from '@angular/core';

import { SportsService } from '@core/sports/sports.service';

/**
 * Reactively writes the active sport's accent color onto
 * `document.documentElement` as `--brijjsports-sport-accent`.
 *
 * Where it applies (the contract):
 *  - Sidebar active-nav indicator (left border + tinted bg + label color)
 *  - `app-sport-chip` selected state
 *  - `app-scoreboard` accent rule
 *
 * Where it does NOT apply:
 *  - Primary CTAs / form fields / toolbar (use `--primary` / `--brijjsports-blue`)
 *  - Status colors (success/warning/danger keep their own semantics)
 *  - Tenant accent (separate token — `--brijjsports-tenant-accent`)
 *
 * Falls back to the tenant accent when no sport is registered.
 */
@Injectable({ providedIn: 'root' })
export class SportThemeService {
  private readonly sports = inject(SportsService);
  private readonly doc = inject(DOCUMENT);

  /** Call once during app bootstrap to start syncing tokens. */
  init(): void {
    effect(() => {
      const sport = this.sports.currentSport();
      const root = this.doc.documentElement;
      // `colorToken` is a CSS variable name like '--brijjsports-blue'. Resolve via
      // var() so theming stays a single source of truth.
      root.style.setProperty('--brijjsports-sport-accent', `var(${sport.colorToken})`);
    });
  }
}
